import { createContext, useContext, useEffect, useState, ReactNode, useMemo, useCallback } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../services/firebase";
import { Globals } from "../globals";
import { useAuth } from "../hooks/useAuth";
import { applyGemeindeLanguage } from "../i18n/gemeindeLanguage";
import Gemeinde, { gemeindeConverter } from "../DTOs/Gemeinde";

const SELECTED_GEMEINDE_KEY = "serva_selected_gemeinde";

interface GemeindeContextValue {
    /** The Gemeinde (tenant) whose data is currently shown, or null. */
    activeGemeindeId: string | null;
    /** The loaded active Gemeinde document (for branding/settings), or null. */
    activeGemeinde: Gemeinde | null;
    /** Superadmin only: switch the active Gemeinde (persisted in localStorage). */
    setActiveGemeindeId: (id: string | null) => void;
    loadingGemeinde: boolean;
    /** Re-fetch the active Gemeinde document (after an edit). */
    refreshGemeinde: () => void;
}

const GemeindeContext = createContext<GemeindeContextValue | undefined>(undefined);

/**
 * Provides the active Gemeinde (tenant) to the app. Members and Gemeinde admins
 * are pinned to their own Gemeinde (from the gemeindeId claim). The superadmin
 * has no Gemeinde of their own and selects one via the Gemeinde switcher; the
 * selection is persisted so it survives reloads.
 */
export function GemeindeProvider({ children }: { children: ReactNode }) {
    const { isSuperAdmin, gemeindeId, loading: authLoading } = useAuth();
    const [selectedId, setSelectedId] = useState<string | null>(() => {
        try {
            return localStorage.getItem(SELECTED_GEMEINDE_KEY);
        } catch {
            return null;
        }
    });
    const [activeGemeinde, setActiveGemeinde] = useState<Gemeinde | null>(null);
    const [loadingGemeinde, setLoadingGemeinde] = useState(false);
    const [reloadFlag, setReloadFlag] = useState(0);

    // Superadmin picks a Gemeinde; everyone else is bound to their claim.
    const activeGemeindeId = isSuperAdmin ? selectedId : gemeindeId;

    const setActiveGemeindeId = useCallback((id: string | null) => {
        setSelectedId(id);
        try {
            if (id) localStorage.setItem(SELECTED_GEMEINDE_KEY, id);
            else localStorage.removeItem(SELECTED_GEMEINDE_KEY);
        } catch {
            /* ignore storage errors */
        }
    }, []);

    const refreshGemeinde = useCallback(() => setReloadFlag((n) => n + 1), []);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            if (authLoading || !activeGemeindeId) {
                setActiveGemeinde(null);
                return;
            }
            setLoadingGemeinde(true);
            try {
                const snap = await getDoc(
                    doc(db, Globals.GEMEINDEN_COLLECTION, activeGemeindeId).withConverter(gemeindeConverter)
                );
                if (!cancelled) setActiveGemeinde(snap.exists() ? snap.data() : null);
            } catch {
                if (!cancelled) setActiveGemeinde(null);
            } finally {
                if (!cancelled) setLoadingGemeinde(false);
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [activeGemeindeId, authLoading, reloadFlag]);

    // The Gemeinde's language is the default until the person picks one (GOD-50).
    const gemeindeLanguage = activeGemeinde?.defaultLanguage;
    useEffect(() => {
        applyGemeindeLanguage(gemeindeLanguage);
    }, [gemeindeLanguage]);

    const value = useMemo<GemeindeContextValue>(
        () => ({
            activeGemeindeId: activeGemeindeId ?? null,
            activeGemeinde,
            setActiveGemeindeId,
            loadingGemeinde,
            refreshGemeinde,
        }),
        [activeGemeindeId, activeGemeinde, setActiveGemeindeId, loadingGemeinde, refreshGemeinde]
    );

    return <GemeindeContext.Provider value={value}>{children}</GemeindeContext.Provider>;
}

export function useGemeinde(): GemeindeContextValue {
    const ctx = useContext(GemeindeContext);
    if (!ctx) throw new Error("useGemeinde must be used within a GemeindeProvider");
    return ctx;
}
